import { useRef, useState } from 'react';
import { CommentIcon, DownloadIcon, HeartIcon, PlayIcon } from './Icons';
import { api, photoUrl, thumbUrl, type Like, type Photo } from '../lib/api';
import { Avatar } from './Avatar';

/**
 * A photograph-led feed. Social state arrives in phase two; these cards already
 * reserve the familiar actions so the layout does not need to change later.
 */
export function FeedCards({
  photos,
  onSelect,
  onUpdate,
}: {
  photos: Photo[];
  onSelect: (photo: Photo, index: number) => void;
  onUpdate: (fileId: string, update: Pick<Photo, 'likeCount' | 'likedByMe'>) => void;
}) {
  return (
    <div className="feed-cards">
      {photos.map((photo, index) => (
        <FeedCard key={photo.postId || photo.fileId || photo.path} photo={photo} priority={index === 0} onSelect={() => onSelect(photo, index)} onUpdate={onUpdate} />
      ))}
    </div>
  );
}

function FeedCard({ photo, priority, onSelect, onUpdate }: { photo: Photo; priority: boolean; onSelect: () => void; onUpdate: (fileId: string, update: Pick<Photo, 'likeCount' | 'likedByMe'>) => void }) {
  const [loaded, setLoaded] = useState(false);
  const [likeSheet, setLikeSheet] = useState<Like[] | null>(null);
  const [liking, setLiking] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const tapTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const author = photo.owner || 'Family';

  const toggleLike = async () => {
    if (liking) return;

    const previous = {
      likedByMe: Boolean(photo.likedByMe),
      likeCount: photo.likeCount ?? 0,
    };
    const optimistic = {
      likedByMe: !previous.likedByMe,
      likeCount: Math.max(0, previous.likeCount + (previous.likedByMe ? -1 : 1)),
    };

    // The home server can take seconds to confirm a tiny write. Reflect the
    // person's intent now, then reconcile with the authoritative response.
    onUpdate(photo.fileId, optimistic);
    setLiking(true);
    setActionError(null);
    try {
      const result = previous.likedByMe
        ? await api.unlike(photo.fileId)
        : await api.like(photo.fileId);
      onUpdate(photo.fileId, result);
    } catch (error) {
      onUpdate(photo.fileId, previous);
      setActionError(error instanceof Error
        ? `${error.message} Your like was restored to its previous state.`
        : 'Could not update this like. It was restored to its previous state.');
    } finally {
      setLiking(false);
    }
  };

  const openLikes = async () => {
    if (!photo.likeCount) return;
    setActionError(null);
    try {
      const result = await api.likes(photo.fileId);
      onUpdate(photo.fileId, result);
      setLikeSheet(result.likes);
    } catch (error) {
      setActionError(error instanceof Error ? error.message : 'Could not load likes.');
    }
  };

  return (
    <article className="feed-card">
      <div className="post-heading">
        <Avatar userId={photo.ownerId} displayName={author} size={38} className="post-avatar" />
        <div className="post-byline">
          <strong>{author}</strong>
          <span>{relativeTime(photo.lastModified)}{photo.audience ? ` · ${photo.audience.label}` : ''}</span>
        </div>
      </div>

      <button
        type="button"
        className="feed-media"
        onClick={() => {
          if (tapTimer.current) {
            window.clearTimeout(tapTimer.current);
            tapTimer.current = null;
            void toggleLike();
            return;
          }
          tapTimer.current = window.setTimeout(() => {
            tapTimer.current = null;
            onSelect();
          }, 240);
        }}
        aria-label={`Open ${photo.name}`}
      >
        <img
          src={thumbUrl(photo.fileId, 1200)}
          alt={photo.name}
          loading={priority ? 'eager' : 'lazy'}
          fetchPriority={priority ? 'high' : 'auto'}
          decoding="async"
          className={loaded ? 'loaded' : ''}
          onLoad={() => setLoaded(true)}
          onError={() => setLoaded(true)}
        />
        {photo.isVideo && <span className="feed-video"><PlayIcon /> Video</span>}
      </button>

      <div className="post-actions" aria-label="Photo actions">
        <button type="button" className={`post-action ${photo.likedByMe ? 'liked' : ''} ${liking ? 'syncing' : ''}`} onClick={() => void toggleLike()} disabled={liking} aria-label={photo.likedByMe ? 'Unlike' : 'Like'} aria-busy={liking}>
          <HeartIcon />
        </button>
        <button type="button" className="post-action" onClick={onSelect} aria-label="View comments">
          <CommentIcon />
        </button>
        <a
          className="post-action"
          href={photoUrl(photo)}
          download={photo.name}
          aria-label={`Download ${photo.name}`}
        >
          <DownloadIcon />
        </a>
      </div>

      <button
        type="button"
        className="post-like-count"
        onClick={() => void (photo.likeCount ? openLikes() : toggleLike())}
        disabled={liking}
        aria-label={photo.likeCount ? `See who liked this, ${photo.likeCount} ${photo.likeCount === 1 ? 'like' : 'likes'}` : 'Be the first to like this'}
      >
        {photo.likeCount ? `${photo.likeCount} ${photo.likeCount === 1 ? 'like' : 'likes'}` : 'Be the first to like this'}
      </button>
      {actionError && <p className="post-action-error" role="status">{actionError}</p>}
      {photo.caption && <p className="post-caption">
        <strong>{author}</strong> {photo.caption}
      </p>}
      <button type="button" className="post-comments" onClick={onSelect}>
        View comments
      </button>
      {likeSheet && <LikesSheet likes={likeSheet} onClose={() => setLikeSheet(null)} />}
    </article>
  );
}

function LikesSheet({ likes, onClose }: { likes: Like[]; onClose: () => void }) {
  return (
    <div className="likes-sheet-backdrop" role="presentation" onMouseDown={onClose}>
      <section className="likes-sheet" role="dialog" aria-modal="true" aria-label="Liked by" onMouseDown={(event) => event.stopPropagation()}>
        <div className="likes-sheet-head"><strong>Liked by</strong><button type="button" onClick={onClose}>Done</button></div>
        {likes.map((like) => <div className="liked-person" key={like.userId}><span>{initials(like.displayName)}</span><strong>{like.displayName}</strong></div>)}
      </section>
    </div>
  );
}

function initials(name: string): string {
  return name
    .split(/[\s._-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('') || 'F';
}

function relativeTime(timestamp: number): string {
  const seconds = Math.max(0, Math.round((Date.now() - timestamp) / 1000));
  if (seconds < 60) return 'Just now';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.round(hours / 24);
  if (days < 7) return `${days}d`;
  return new Date(timestamp).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}
